/* SPEC-453 — the glossary LOOKUP CONTRACT, as pure functions.
 *
 * A ⓘ popover resolves its meaning by key: a state pill looks itself up by the exact state
 * token the API sent it (`INSUFFICIENT_DATA`), a headline tile by a term name ("Promo uplift").
 * Getting that resolution wrong does not show nothing — it shows the WRONG MEANING, which is
 * the one outcome worse than no explanation at all. So the rules live here, pure and tested
 * (`glossaryIndex.test.ts`), with the React plumbing in `hooks/useGlossary.ts`.
 */

/** One glossary entry as `GET /api/governance/glossary` serves it. Mirrors `GlossaryTerm` in
 *  `lib/api.ts`, declared structurally here so this module stays free of the API layer (and
 *  so the test can run it under plain node, with no path alias to resolve). */
export interface GlossaryEntryLike {
  term: string;
  aliases: string[];
  definition: string;
  on_screen?: string | null;
  pending_ruling?: string | null;
}

export interface GlossaryIndexOf<T extends GlossaryEntryLike> {
  byKey: Map<string, T>;
  terms: T[];
}

/** Build the lookup index in TWO PASSES, and the order is load-bearing.
 *
 *  Pass 1 claims EXACT keys (the term and each alias verbatim). State tokens are UPPER_SNAKE
 *  and unique to their own entry, so they are claimed here before anything else can take them.
 *
 *  Pass 2 adds lower-cased forms for human lookups, and may never displace a pass-1 key.
 *
 *  Why it matters: the "Data freshness / as-of" entry carries the alias `stale`, and the
 *  honest-state entry for the STALE pill carries `STALE` plus its own lower-cased `stale`. In
 *  a single case-folded pass, whichever entry came first in the payload would own `stale` AND
 *  `STALE` — and a STALE pill could open a popover explaining data freshness in general
 *  instead of what that pill means. Two passes make the exact token always win for the pill,
 *  while a human typing "stale" still gets the freshness entry.
 */
export function buildGlossaryIndex<T extends GlossaryEntryLike>(terms: T[]): GlossaryIndexOf<T> {
  const byKey = new Map<string, T>();
  for (const t of terms) {
    for (const raw of [t.term, ...(t.aliases || [])]) {
      if (raw && !byKey.has(raw)) byKey.set(raw, t);
    }
  }
  for (const t of terms) {
    for (const raw of [t.term, ...(t.aliases || [])]) {
      if (!raw) continue;
      const key = raw.toLowerCase();
      if (!byKey.has(key)) byKey.set(key, t);
    }
  }
  return { byKey, terms };
}

/** The entry for a term or state token, or null when the glossary does not define it.
 *  Verbatim first (state tokens are upper-case), then lower-cased. Null means the caller
 *  renders NO affordance — never an empty popover. */
export function lookupGlossary<T extends GlossaryEntryLike>(
  index: GlossaryIndexOf<T>,
  key: string | null | undefined,
): T | null {
  if (!key) return null;
  return index.byKey.get(key) ?? index.byKey.get(key.toLowerCase()) ?? null;
}
